import { numberParam } from '../types/checks.js';
import type { CheckResult, ValidationCheck } from '../types/checks.js';
import { frameGeometry, judgeRange, plainResult } from './support.js';

/**
 * Pixel dimensions check.
 *
 * A rectangle covering the whole frame is attached as geometry, so the overlay
 * can highlight the image boundary when the size is wrong.
 */
export const imageSizeCheck: ValidationCheck = {
  id: 'image_size',
  title: 'Размер изображения',
  description: 'Проверяет ширину и высоту в пикселях.',
  kind: 'technical',
  requires: ['imageMeta'],

  run: (ctx): CheckResult => {
    const minWidth = numberParam(ctx.rule.params, 'minWidth', 600);
    const maxWidth = numberParam(ctx.rule.params, 'maxWidth', 1200);
    const minHeight = numberParam(ctx.rule.params, 'minHeight', 600);
    const maxHeight = numberParam(ctx.rule.params, 'maxHeight', 1200);
    const { width, height } = ctx.image;

    const widthVerdict = judgeRange({ value: width, min: minWidth, max: maxWidth, warningMargin: 0.02 });
    const heightVerdict = judgeRange({
      value: height,
      min: minHeight,
      max: maxHeight,
      warningMargin: 0.02,
    });

    const worst =
      widthVerdict.status === 'fail' || heightVerdict.status === 'fail'
        ? 'fail'
        : widthVerdict.status === 'warning' || heightVerdict.status === 'warning'
          ? 'warning'
          : widthVerdict.status === 'uncertain' || heightVerdict.status === 'uncertain'
            ? 'uncertain'
            : 'pass';

    const digitalNote = ctx.profile.image.digital?.source;
    const requirement =
      `Требуется ${minWidth}–${maxWidth} px по ширине и ${minHeight}–${maxHeight} px по высоте.` +
      (digitalNote ? ` ${digitalNote}` : '');
    const tooSmall = width < minWidth || height < minHeight;
    const tooLarge = width > maxWidth || height > maxHeight;

    return plainResult({
      id: imageSizeCheck.id,
      title: imageSizeCheck.title,
      status: worst,
      severity: ctx.rule.severity,
      value: Math.min(width, height),
      min: Math.min(minWidth, minHeight),
      max: Math.max(maxWidth, maxHeight),
      unit: 'px',
      message:
        worst === 'pass'
          ? `Размер соответствует: ваше ${width}×${height} px.`
          : tooSmall
            ? `Изображение слишком маленькое: ваше ${width}×${height} px, требуется от ${minWidth}×${minHeight} px.`
            : tooLarge
              ? `Изображение слишком большое: ваше ${width}×${height} px, требуется до ${maxWidth}×${maxHeight} px.`
              : `Размер ${width}×${height} px близок к границе допустимого диапазона.`,
      requirement,
      fixability: tooSmall ? 'retake' : 'fixable',
      geometry: frameGeometry(),
      confidence: 1,
      details: { width, height, minWidth, maxWidth, minHeight, maxHeight, displayValue: `${width}×${height} px` },
    });
  },
};
